import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'next-i18next';

import { useAppDispatch, useAppStore } from 'src/app/hooks';
import DefaultDialog from 'components/UI/Dialogs/DefaultDialog/DefaultDialog';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';
import ProposalEntity from 'src/db/entities/proposal/ProposalEntity';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import {
  patchProformaInvoice,
  selectProformaInvoices,
} from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { selectProposalSettings } from 'src/slices/proposalsSlice';
import { selectCurrentUserMemberData } from 'src/slices/workspacesSlice';
import { cloneSettingsAsNewForInvoice } from 'src/features/proformaInvoices/ProformaInvoiceForm/mappers';
import {
  findLinkedInvoice,
  isInvoiceUpToDateWithQuote,
  mapQuoteToInvoiceUpdatePayload,
} from './quoteToInvoice';

interface PendingPrompt {
  invoice: ProformaInvoiceEntity;
  quote: ProposalEntity;
  resolve: () => void;
}

/**
 * After a quote is saved, asks "Update linked invoice?" when the quote has an
 * invoice whose content differs. The invoice changes only on "Update"; "Skip"
 * leaves it as it is. `offerInvoiceUpdate` resolves once the user has answered
 * (and the update finished), so callers can close the form afterwards.
 */
export default function useLinkedInvoicePrompt() {
  const { t } = useTranslation('proposal-form');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const [pending, setPending] = useState<PendingPrompt | null>(null);

  const offerInvoiceUpdate = (quote: ProposalEntity): Promise<void> => {
    const invoice = findLinkedInvoice(selectProformaInvoices(store.getState()), quote.uuid);

    if (!invoice || isInvoiceUpToDateWithQuote(invoice, quote)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => setPending({ invoice, quote, resolve }));
  };

  const handleSkip = () => {
    pending?.resolve();
    setPending(null);
  };

  const handleUpdate = async () => {
    if (!pending) {
      return;
    }

    const { invoice, quote, resolve } = pending;
    setPending(null);

    try {
      const state = store.getState();
      const payload = mapQuoteToInvoiceUpdatePayload(
        invoice,
        quote,
        cloneSettingsAsNewForInvoice(selectProposalSettings(state)),
        selectCurrentUserMemberData(state)?.email ?? invoice.updatedBy ?? null
      );

      await dispatch(patchProformaInvoice(payload)).unwrap();

      enqueueSnackbar({
        message: t('linkedInvoiceDialog.updated'),
        variant: MessageType.Success,
      });
    } catch (error) {
      enqueueSnackbar({
        message: t('linkedInvoiceDialog.updateFailed'),
        variant: MessageType.Error,
      });
    } finally {
      resolve();
    }
  };

  const dialogUI = pending ? (
    <DefaultDialog
      open
      title={t('linkedInvoiceDialog.title')}
      description={t('linkedInvoiceDialog.body')}
      confirmText={t('linkedInvoiceDialog.update')}
      rejectText={t('linkedInvoiceDialog.skip')}
      onSuccess={handleUpdate}
      onClose={handleSkip}
      handleSuccessWithoutClose
    />
  ) : null;

  return { offerInvoiceUpdate, dialogUI };
}
